import React, { useState, useEffect } from "react";
import { Form, Button, Card, Alert, Spinner } from "react-bootstrap";
import { useLocation } from "react-router-dom"; // Hook to catch the passed state
import axios from "axios";

const ManualAttendanceForm = (props) => {
  const location = useLocation();
  
  // 1. Extract potential incoming route states
  const passedEmployee = location.state?.employee;
  const passedStudent = location.state?.student;
  const hasPassedData = !!(passedEmployee || passedStudent);

  // 2. Initialize state intelligently based on the context provided
  const [userType, setUserType] = useState(passedEmployee ? "employee" : "student");
  
  // Determine correct ID and Name mapping based on what was passed
  const [studentId, setStudentId] = useState(() => {
    if (passedEmployee) return passedEmployee.EmployeeId || "";
    if (passedStudent) return passedStudent.studentId || passedStudent.rollNo || "";
    return "";
  });

  const [studentName, setStudentName] = useState(() => {
    if (passedEmployee) return passedEmployee.Full_Name || "";
    if (passedStudent) {
      return passedStudent.Full_Name || 
        [passedStudent.firstName, passedStudent.middleName, passedStudent.lastName].filter(Boolean).join(" ") || 
        "";
    }
    return "";
  });

  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split("T")[0]);
  const [hours, setHours] = useState(""); 
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });

  // Sync state if location parameters update dynamically
  useEffect(() => {
    if (passedEmployee) {
      setUserType("employee");
      setStudentId(passedEmployee.EmployeeId || "");
      setStudentName(passedEmployee.Full_Name || "");
    } else if (passedStudent) {
      setUserType("student");
      setStudentId(passedStudent.studentId || passedStudent.rollNo || "");
      setStudentName(
        passedStudent.Full_Name || 
        [passedStudent.firstName, passedStudent.middleName, passedStudent.lastName].filter(Boolean).join(" ") || 
        ""
      );
    }
  }, [passedEmployee, passedStudent]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: "", text: "" });

    const token = props.userData?.token; 
    const branch = props.branch || props.loginBranch || "Rehoboth";

    try {
      await axios.post(
        `${process.env.REACT_APP_COLLEGE_ERP_API}mark-attendance-manually`,
        {
          branch: branch,
          studentId: studentId,
          studentName: studentName,
          customDate: selectedDate,
          hours: userType === "employee" && hours ? hours : null, // Send hours only for employees
          photoKey: "MANUAL_ENTRY_NO_FACEID"
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setMessage({
        type: "success",
        text: `Attendance successfully marked for ${userType === "student" ? "Student" : "Employee"} (${studentId}) on ${selectedDate}.`
      });
      
      // Reset fields only if we didn't land from a direct profile link
      if (!hasPassedData) {
        setStudentId("");
        setStudentName("");
      }
      setHours("");
    } catch (error) {
      console.error("Error marking attendance:", error);
      setMessage({
        type: "danger",
        text: error.response?.data?.message || "Failed to mark attendance. Please try again."
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="shadow-sm mx-auto my-4" style={{ maxWidth: "600px" }}>
      <Card.Header className="bg-primary text-white py-3 d-flex justify-content-between align-items-center">
        <h5 className="mb-0">Manual Attendance Management</h5>
        {hasPassedData && (
          <span className="badge bg-warning text-dark">
            Pre-loaded {userType === "employee" ? "Employee" : "Student"}
          </span>
        )}
      </Card.Header>
      <Card.Body className="p-4">
        
        {message.text && (
          <Alert variant={message.type} onClose={() => setMessage({ type: "", text: "" })} dismissible>
            {message.text}
          </Alert>
        )}

        <Form onSubmit={handleSubmit}>
          {/* User Type Selection */}
          <Form.Group className="mb-3">
            <Form.Label className="fw-bold">User Type</Form.Label>
            <div className="d-flex gap-3">
              <Form.Check
                type="radio"
                id="type-student"
                label="Student"
                name="userType"
                checked={userType === "student"}
                onChange={() => setUserType("student")}
                disabled={hasPassedData} // Locked if a profile was linked directly
              />
              <Form.Check
                type="radio"
                id="type-employee"
                label="Employee"
                name="userType"
                checked={userType === "employee"}
                onChange={() => setUserType("employee")}
                disabled={hasPassedData}
              />
            </div>
          </Form.Group>

          {/* ID Input */}
          <Form.Group className="mb-3" controlId="formStudentId">
            <Form.Label className="fw-bold">
              {userType === "student" ? "Student ID / Roll No" : "Employee ID"}
            </Form.Label>
            <Form.Control
              type="text"
              placeholder={`Enter ${userType === "student" ? "Student" : "Employee"} ID`}
              value={studentId}
              onChange={(e) => setStudentId(e.target.value)}
              required
              disabled={hasPassedData} // Locked to prevent accidental overwrites
            />
          </Form.Group>

          {/* Name Input */}
          <Form.Group className="mb-3" controlId="formStudentName">
            <Form.Label className="fw-bold">Full Name</Form.Label>
            <Form.Control
              type="text"
              placeholder="Auto-fetched on form processing"
              value={studentName}
              onChange={(e) => setStudentName(e.target.value)}
              disabled={hasPassedData}
            />
          </Form.Group>

          {/* Date Picker */}
          <Form.Group className="mb-3" controlId="formAttendanceDate">
            <Form.Label className="fw-bold">Select Date</Form.Label>
            <Form.Control
              type="date"
              max={new Date().toISOString().split("T")[0]}
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              required
            />
          </Form.Group>

          {/* Conditionally Render Hours Parameter ONLY for Employees */}
          {userType === "employee" && (
            <Form.Group className="mb-4" controlId="formWorkHours">
              <Form.Label className="fw-bold">Total Work Hours (Optional)</Form.Label>
              <Form.Control
                type="number"
                step="0.5"
                min="0"
                max="24"
                placeholder="e.g. 8, 7.5"
                value={hours}
                onChange={(e) => setHours(e.target.value)}
              />
              <Form.Text className="text-muted">
                Leave empty to log standard system entry without explicit hours calculations.
              </Form.Text>
            </Form.Group>
          )}

          {/* Action Button */}
          <div className="d-grid">
            <Button variant={hasPassedData ? "warning" : "success"} type="submit" size="lg" disabled={loading}>
              {loading ? (
                <>
                  <Spinner as="span" animation="border" size="sm" role="status" className="me-2" />
                  Updating Record...
                </>
              ) : (
                "Save Attendance Record"
              )}
            </Button>
          </div>
        </Form>
      </Card.Body>
    </Card>
  );
};

export default ManualAttendanceForm;